/**
 * @fileoverview Plugin to convert list of menu of actions including a button
 *               for the primary action and a toggleable dropdown
 *
 * @author       Nicholas Cassleman (ncassleman@yext.com)
 */

goog.module('yext.plugins.ActionMenu');
goog.module.declareLegacyNamespace();

goog.require('yext');

/**
 * Constructor for an ActionMenu
 *
 * @constructor
 *
 * @param {!jQuery} $container ActionMenu container, which contains a list of items
 * @param {!Object=} options Options to customize ActionMenu
 */
const ActionMenu = function($container, options) {
  if ($container.data('actionmenu')) {
    return;
  }

  this.$container = $container;
  this.$dropdown = this.$container.find('ul, ol').first();
  this.$primaryAction = undefined;
  this.$dropdownToggler = undefined;
  this.hasDropdown = undefined;
  this.isMenuOpen = undefined;
  this.hasPrimaryAction = undefined;
  this.isCompact = this.$container.data('compact');
  this.options = Object.freeze($.extend(
    true,
    {},
    ActionMenu.DEFAULT_OPTIONS(this),
    options));

  if (this.$dropdown.children().length > 0) {
    this.wrapBareMenuItems();
    this.extractPrimaryAction();
    this.initializeAriaRoles();
    this.initializeStyles();
    this.bindEventListeners();
  }

  this.$container.data('actionmenu', this);
};

/**
 * Default options for an ActionMenu may be overriden via ActionMenu's constructor
 *
 * TODO(ncassleman): Change position depending on ActionMenu mode (when
 * implemented, must choose between modes PRIMARY, DROPDOWN, COLLAPSED)
 *
 * @param  {ActionMenu} actionMenu Options for provided ActionMenu
 *
 * @return {!Object}
 */
ActionMenu.DEFAULT_OPTIONS = function(actionMenu) {
  return {
    'position': {
      'my': 'right top',
      'at': 'right bottom',
      'of': actionMenu.$container,
      'collision': 'flip',
      'offset': '0 3',
    },
  };
};

/**
 * Wraps list items that do not contain an anchor or button in a button element to ensure
 * that users can naturally shift focus to list items
 */
ActionMenu.prototype.wrapBareMenuItems = function() {
  this.$dropdown.children().each(function() {
    var $this = $(this);

    if ($this.children().first('a, button').length === 0) {
      // inline type required for IE8
      // we want generic type 'button' instead of default 'sumbit'
      var $button = $('<button type="button">').html($this.contents());
      $this.html($button);
    }
  });
};

/**
 * If data-toggler-text exists, make toggler full width. Display the text in data attr.
 *
 * Otherwse, extract the first item of the list and place it outside the remaining
 * items as the primary action.
 *
 * Item contents expected to be wrapped in <a> or <button> elements.
 */
ActionMenu.prototype.extractPrimaryAction = function() {
  var togglerText = this.$container.data('togglerText');
  this.hasPrimaryAction = !this.isCompact && togglerText === undefined;

  if (this.hasPrimaryAction) {
    var $item = this.$dropdown.children().first().remove();
    this.$primaryAction = $item.find('a, button')
      .first()
      .addClass($item.prop('class'))
      .insertBefore(this.$dropdown);
  } else {
    this.$primaryAction = null;
  }

  this.hasDropdown = this.$dropdown.children().length > 0;

  if (this.hasDropdown) {
    this.$dropdownToggler = $('<button type="button">').insertBefore(this.$dropdown);
    if (!this.hasPrimaryAction && !this.isCompact) {
      this.$dropdownToggler.html(
        '<span class="action-menu-toggler-text">' + togglerText + '</span>');
    }
  } else {
    this.$dropdown.remove();
    this.$dropdown = null;
  }
};

/**
 * Sets the role of the menu and menu items. The menu role will be assigned to the
 * <ul> or <ol> and the menuitem role will be assigned to the first <a> or <button>
 * in each list item
 */
ActionMenu.prototype.initializeAriaRoles = function() {
  if (this.hasDropdown) {
    this.$dropdown.prop('role', 'menu');
    this.$dropdown.children().each(function() {
      $(this).children().first('a, button').prop('role', 'menuitem');
    });
  }
};

/**
 * Set display styles for the action menu. Assumes all elements have been
 * created and arranged
 */
ActionMenu.prototype.initializeStyles = function() {
  this.$container.addClass('action-menu-container');

  if (this.hasPrimaryAction) {
    this.$primaryAction.addClass('action-menu-button');
    this.$primaryAction.attr('tid', 'primary-action-button');
    if (!this.hasDropdown) {
      this.$primaryAction.addClass('sl-onlyButton');
    }
  }

  if (this.hasDropdown) {
    if (this.isCompact) {
      this.$container.addClass('compact');
      this.$dropdownToggler.addClass('action-menu-compact-toggler');
      this.$dropdownToggler
        .attr('tabIndex', '0')
        .attr('aria-label', yext.msg('Action Menu'))
        .attr('aria-pressed', 'false')
        .attr('role', 'button');
    } else {
      this.$dropdownToggler.addClass('action-menu-button');
      this.$dropdownToggler.attr('tid', 'action-menu-toggler');

      if (this.hasPrimaryAction) {
        this.$primaryAction.addClass('primary');
        this.$dropdownToggler.addClass('toggler');
        this.$dropdownToggler.attr('aria-label', yext.msg('More Actions'));
      } else {
        this.$dropdownToggler.addClass('full-toggler');
      }
    }

    this.$dropdown.addClass('action-menu-dropdown').hide();
    this.$dropdown.children().each(function() {
      $(this).children().first('a, button').addClass('action-menu-item');
    });
  }
};

/**
 * Binds event listeners for toggling dropdown display and controlling focus
 * via the keyboard
 */
ActionMenu.prototype.bindEventListeners = function() {
  var self = this;

  if (this.hasDropdown) {
    // toggle menu on click
    this.$dropdownToggler.on('click', function() {
      if (self.isMenuOpen) {
        self.closeMenu();
      } else {
        self.openMenu();
      }
    });

    // NOTE(ncassleman): would ideally check if container had lost focus,
    //  but browser compatibility of solutions is poor
    $(document).on('click', function(e) {
      var isTargetInMenu = $.contains(self.$container.get(0), e.target);
      if (self.isMenuOpen && !isTargetInMenu) {
        self.closeMenu();
      }
    });

    // close menu on escape key
    this.$container.on('keydown', function(e) {
      switch (e.keyCode) {
        case $.ui.keyCode.ESCAPE:
          self.closeMenu();
          self.$dropdownToggler.focus();
          break;
        default:
          break;
      }
    });

    // close menu on action selection
    this.$dropdown.on('click', function() {
      self.closeMenu();
    });
    if (this.hasPrimaryAction) {
      this.$primaryAction.on('click', function() {
        self.closeMenu();
      });
    }
  }
};

/**
 * Opens the menu
 */
ActionMenu.prototype.openMenu = function() {
  if (this.hasDropdown) {
    this.isMenuOpen = true;
    this.$dropdownToggler.addClass('toggler-open');
    this.$dropdown.show()
      .css({
        'min-width': this.$container.outerWidth(),
      })
      .position(this.options.position);
  }
};

/**
 * Closes the menu
 */
ActionMenu.prototype.closeMenu = function() {
  if (this.hasDropdown) {
    this.isMenuOpen = false;
    this.$dropdownToggler.removeClass('toggler-open');
    this.$dropdown.hide();
  }
};

/**
 * Creates a new ActionMenu from an appropriate container
 *
 * @param  {!jQuery} $container Container for action menu
 * @param  {!Object} options    Options to customize ActionMenu
 *
 * @return {(ActionMenu|Array<ActionMenu>)}
 */
ActionMenu.initialize = function($container, options) {
  if ($container.length === 1) {
    return new ActionMenu($container, options);
  } else {
    return ActionMenu.initializeAll($container, options, true);
  }
};

/**
 * Creates a new ActionMenu from appropriate jQuery containers and returns
 * the instantiated objects. Will also normalize widths unless otherwise specified
 *
 * @param  {!jQuery}                         $containers    Containers for action menus
 * @param  {boolean}                        doNotNormalize True if menu widths shouldn't be normalized
 * @param  {!Object}                         options        Options to customize ActionMenu
 * @return {!Array<ActionMenu>}                Returns the newly instantiated object
 */
ActionMenu.initializeAll = function($containers, doNotNormalize, options) {
  var menus = [];

  $containers.each(function() {
    menus.push(new ActionMenu($(this), options));
  });

  if (doNotNormalize !== true && $containers.length > 1) {
    ActionMenu.normalizeButtonWidth($containers);
  }

  return menus;
};

/**
 * Sets the width of each action menu primary button to the same width as the widest
 * action menu primary button in $containers
 *
 * @param  {!jQuery} $containers Containers for each action menu
 */
ActionMenu.normalizeButtonWidth = function($containers) {
  var widths = $containers.map(function() {
    return $(this).innerWidth();
  }).get();
  var maxWidth = Math.max.apply(null, widths);
  if (maxWidth > 0) {
    $containers.each(function() {
      var menu = $(this).data('actionmenu');
      if (menu.$primaryAction) {
        var dropdownBtnWidth = menu.hasDropdown ? menu.$dropdownToggler.width() : 0;
        menu.$primaryAction.innerWidth(maxWidth - dropdownBtnWidth);
      }
    });
  }
};

/**
 * Displays the ActionMenu in the DOM
 *
 * Useful for preventing FOUC. Hide container before initializing, then call this method
 * after intialized
 */
ActionMenu.prototype.show = function() {
  if (this.$container) {
    this.$container.show();
  }
};

/**
 * Hides the ActionMenu in the DOM
 */
ActionMenu.prototype.hide = function() {
  if (this.$container) {
    this.$container.hide();
  }
};

exports = ActionMenu;
